import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [showAllCourses, setShowAllCourses] = useState(true); // Changed from false to true
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formCourseName, setFormCourseName] = useState('');
  const [formData, setFormData] = useState({ fullName: '', mobileNumber: '', email: '' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // const [showAllCourses, setShowAllCourses] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch('https://api.dikshasea.in/api/courses');
        if (!response.ok) throw new Error('Failed to fetch courses');
        const data = await response.json();
        data.sort((a, b) => a.coursename.localeCompare(b.coursename));
        setCourses(data);
        if (data.length > 0) setSelectedCourseId(data[0].id);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setFormData({ fullName: '', mobileNumber: '', email: '' });
  }, []);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && showModal) closeModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showModal, closeModal]);

  const handleEnrollClick = (courseName) => {
    setFormCourseName(courseName);
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobileNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const mobilePattern = /^\d{10}$/;
    if (!mobilePattern.test(formData.mobileNumber)) {
      alert('Please enter a valid 10-digit mobile number.');
      return;
    }

    try {
      const response = await fetch('https://api.dikshasea.in/api/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, course: formCourseName }),
      });
      const result = await response.json();
      if (response.ok) {
        alert('Enrollment successful!');
        closeModal();
      } else {
        alert(result.message || 'Enrollment failed');
      }
    } catch (error) {
      console.error(error);
      alert('Error submitting form');
    }
  };

  const isSearching = searchTerm.trim() !== '';

  const filteredSubcourses = useMemo(() => {
    if (!isSearching) return [];
    return courses.flatMap((course) =>
      course.subcourses
        ?.filter((sub) =>
          sub.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map((sub) => ({ ...sub, parentCourse: course.coursename })) || []
    );
  }, [courses, searchTerm, isSearching]);

  const selectedCourse = courses.find((course) => course.id === selectedCourseId);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating - fullStars >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <i key={`full-${i}`} className="fas fa-star text-warning"></i>
      );
    }
    if (hasHalfStar) {
      stars.push(
        <i key="half" className="fas fa-star-half-alt text-warning"></i>
      );
    }
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <i key={`empty-${i}`} className="far fa-star text-warning"></i>
      );
    }
    return stars;
  };

  if (loading) return <p className="text-center py-5">Loading courses...</p>;
  if (error) return <p className="text-center py-5 text-danger">Error: {error}</p>;

  return (
    <div className="container-fluid py-4">
      {/* Centered heading + Search */}
      <div className="d-flex flex-column flex-md-row justify-content-center align-items-center mb-4 gap-3">
        <h3 className="fw-bold mb-0 text-center">Explore Our Courses</h3>
        <div className="position-relative" style={{ maxWidth: '300px', width: '50%' }}>
          <input
            type="text"
            className="form-control ps-5 shadow-sm"
            placeholder="Search all courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '10px', borderRadius: '10px' }}
          />
          <i
            className="fas fa-search position-absolute"
            style={{ top: '50%', left: '12px', transform: 'translateY(-50%)', color: '#aaa' }}
          ></i>
        </div>
      </div>

      {/* Course buttons */}
      {!isSearching && (
  <div className="text-center mb-4">
    <div className="d-flex flex-wrap justify-content-center gap-3">
      <button
        className={`btn ${
          showAllCourses ? 'btn-primary' : 'btn-light'
        } px-4 py-2 border border-secondary rounded-pill fw-semibold`}
        style={{ minWidth: '190px' }}
        onClick={() => {
          setShowAllCourses(true);
          setSelectedCourseId(null);
        }}
      >
        All Courses
      </button>
      {courses.map((course) => (
        <button
          key={course.id}
          className={`btn ${
            selectedCourseId === course.id && !showAllCourses ? 'btn-primary' : 'btn-light'
          } px-4 py-2 border border-secondary rounded-pill fw-semibold`}
          style={{ minWidth: '190px' }}
          onClick={() => {
            setSelectedCourseId(course.id);
            setShowAllCourses(false);
          }}
        >
          {course.coursename}
        </button>
      ))}
    </div>
  </div>
)}

      {/* Subcourses or "no results" / "no subcourses" message */}
      <div className="container">
        {isSearching ? (
          filteredSubcourses.length > 0 ? (
            <div className="row justify-content-center">
              {filteredSubcourses.map((sub, index) => (
                <div
                  key={sub.id || index}
                  className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4 d-flex"
                >
                  <div
                    className="card h-100 shadow-sm w-100"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/${sub.path}`)}
                  >
                    <img
                      src={`/${sub.image}`}
                      alt={sub.name || 'Subcourse'}
                      className="card-img-top"
                      style={{ height: '100px', objectFit: 'cover' }}
                    />
                    <div className="card-body d-flex flex-column">
                      <h6 className="card-title text-center">{sub.name}</h6>
                      <p className="text-center small text-muted">
                        Course: {sub.parentCourse}
                      </p>
                      <p className="mb-2 text-center">
                        {renderStars(sub.rating || 4.5)}{' '}
                        <span className="ms-2">
                          {(sub.rating || 4.5).toFixed(1)} / 5
                        </span>
                      </p>
                      <div className="mt-auto d-flex justify-content-center">
                        <button
                          className="btn btn-sm btn-primary w-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEnrollClick(sub.name);
                          }}
                        >
                          Enroll
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="row justify-content-center" style={{ minHeight: '200px' }}>
              <div className="col-12 d-flex justify-content-center align-items-center">
                <p className="text-center fw-bold fs-5">
                  No results found for "{searchTerm}"
                </p>
              </div>
            </div>
          )
        ) : showAllCourses ? (
          <div className="row justify-content-center">
            {courses.flatMap(course => 
              course.subcourses?.map(sub => (
                <div
                  key={sub.id}
                  className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4 d-flex"
                >
                  <div
                    className="card h-100 shadow-sm w-100"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/${sub.path}`)}
                  >
                    <img
                      src={`/${sub.image}`}
                      alt={sub.name || 'Subcourse'}
                      className="card-img-top"
                      style={{ height: '200px', objectFit: 'cover' }}
                    />
                    <div className="card-body d-flex flex-column">
                      <h6 className="card-title text-center">{sub.name}</h6>
                      <p className="text-center small text-muted">
                        Course: {course.coursename}
                      </p>
                      <p className="mb-2 text-center">
                        {renderStars(sub.rating || 4.5)}{' '}
                        <span className="ms-2">
                          {(sub.rating || 4.5).toFixed(1)} / 5
                        </span>
                      </p>
                      <div className="mt-auto d-flex justify-content-center">
                        <button
                          className="btn btn-sm btn-primary w-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEnrollClick(sub.name);
                          }}
                        >
                          Enroll
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )) || []
            )}
          </div>
        ) : selectedCourse?.subcourses && selectedCourse.subcourses.length > 0 ? (
          <div className="row justify-content-center">
            {selectedCourse.subcourses.map((sub) => (
              <div
                key={sub.id}
                className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4 d-flex"
              >
                <div
                  className="card h-100 shadow-sm w-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/${sub.path}`)}
                >
                  <img
                    src={`/${sub.image}`}
                    alt={sub.name || 'Subcourse'}
                    className="card-img-top"
                    style={{ height: '200px', objectFit: 'cover' }}
                  />
                  <div className="card-body d-flex flex-column">
                    <h6 className="card-title text-center">{sub.name}</h6>
                    <p className="mb-2 text-center">
                      {renderStars(sub.rating || 4.5)}{' '}
                      <span className="ms-2">
                        {(sub.rating || 4.5).toFixed(1)} / 5
                      </span>
                    </p>
                    <div className="mt-auto d-flex justify-content-center">
                      <button
                        className="btn btn-sm btn-primary w-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEnrollClick(sub.name);
                        }}
                      >
                        Enroll
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="row justify-content-center" style={{ minHeight: '200px' }}>
            <div className="col-12 d-flex justify-content-center align-items-center">
              <p className="text-center fw-bold fs-5">
                No subcourses found for "{selectedCourse?.coursename}"
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="modal d-block fade show"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4 d-flex flex-column flex-md-row">
              <div className="w-100 w-md-50 mb-3 mb-md-0">
                <img
                  src="img/enroll.jpg"
                  alt="Enrollment Visual"
                  className="img-fluid h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="w-100 w-md-50 ps-md-4">
                <h6 className="text-primary">Course:</h6>
                <h4 className="fw-bold">{formCourseName}</h4>
                <form onSubmit={handleSubmit} noValidate>
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                  />
                  <input
                    className="form-control mb-3"
                    type="email"
                    name="email"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark me-2"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;